import styled from '@emotion/styled';
import Typography from '@mui/material/Typography';
import slugify from '@sindresorhus/slugify';
import React from 'react';
import { useTranslate } from 'hooks/useTranslate';
import { useNavigate } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import { useDispatch } from 'react-redux';

import ArrowContainedIcon from 'assets/icons/arrow_contained.svg?react';
import ChevronIcon from 'assets/icons/chevron_right.svg?react';
import StarIcon from 'assets/icons/star.svg?react';
import CheckedStarIcon from 'assets/icons/star_checked.svg?react';

import CourseAttributes from 'components/Course/CourseAttributes';
import HiddenLink from 'components/HiddenLink';
import NeedAuthDialog from 'components/NeedAuthDialog';
import { isAuthenticated } from 'helpers/authToken';
import { addCourseToFavorites, removeCourseFromFavorites } from 'actions/educations';

const Card = styled('div')(() => ({
  backgroundColor: 'rgba(250, 250, 250, 1)',
  borderRadius: 18,
  border: '1px solid rgba(10, 10, 10, 1)',
  boxShadow: '4px 4px 0px 0px rgba(10, 10, 10, 1)',
  padding: 18,
  position: 'relative',
  overflow: 'hidden',
  zIndex: 1,
  cursor: 'pointer',
  paddingLeft: 24,
  paddingRight: 24,
  paddingBottom: 45
}));

const LinkStyled = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: '#fff',
  justifyContent: 'space-between',
  color: 'rgba(10, 10, 10, 1)',
  borderRadius: 8,
  padding: 10,
  paddingTop: 16,
  paddingBottom: 16,
  marginLeft: -10,
  marginRight: -10,
  marginBottom: 8,
  cursor: 'pointer'
}));

const LinkTitleStyled = styled(Typography)(() => ({
  fontWeight: 500,
  fontSize: 16,
  lineHeight: '24px',
  color: 'rgba(10, 10, 10, 1)',
  userSelect: 'text',
  WebkitUserSelect: 'text',
  MozUserSelect: 'text',
  msUserSelect: 'text',
  cursor: 'text'
}));

const ListItemTitle = styled(Typography)(() => ({
  fontWeight: 500,
  fontSize: 20,
  lineHeight: '24px',
  marginTop: 15,
  color: 'rgba(68, 68, 68, 1)',
  position: 'relative',
  paddingRight: 45,
  userSelect: 'text',
  WebkitUserSelect: 'text',
  MozUserSelect: 'text',
  msUserSelect: 'text',
  cursor: 'text'
}));

const TagsWrapper = styled('div')(() => ({
  display: 'flex',
  gap: 4,
  marginTop: 8,
  marginBottom: 12,
  userSelect: 'text',
  WebkitUserSelect: 'text',
  MozUserSelect: 'text',
  msUserSelect: 'text'
}));

const Tag = styled('span')(({ type }) => ({
  fontSize: 14,
  color: 'rgba(10, 10, 10, 1)',
  borderRadius: 38,
  padding: '4px 10px',
  fontFamily: 'Rubik-Regular',
  position: 'relative',
  paddingLeft: 20,
  userSelect: 'text',
  WebkitUserSelect: 'text',
  MozUserSelect: 'text',
  msUserSelect: 'text',
  cursor: 'text',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: 10,
    width: 6,
    height: 6,
    marginTop: -3,
    borderRadius: '50%'
  },
  ...((type === 1 || ['Фаховий молодший бакалавр', 'Бакалавр', 'Новачок'].includes(type)) && {
    backgroundColor: 'rgba(121, 201, 18, 0.2)',
    '&:before': {
      backgroundColor: 'rgba(121, 201, 18, 1)'
    }
  }),
  ...((type === 2 || ['Спеціаліст'].includes(type)) && {
    backgroundColor: 'rgba(255, 191, 0, 0.2)',
    '&:before': {
      backgroundColor: 'rgba(255, 191, 0, 1)'
    }
  }),
  ...((type === 3 || ['Магістр', 'Експерт'].includes(type)) && {
    backgroundColor: 'rgba(130, 130, 130, 0.2)',
    '&:before': {
      backgroundColor: 'rgba(130, 130, 130, 1)'
    }
  })
}));

const ArrowContainedIconStyled = styled(ArrowContainedIcon)(() => ({
  position: 'absolute',
  right: 20,
  bottom: 10
}));

const FavoriteButton = styled(IconButton)(() => ({
  position: 'absolute',
  top: -10,
  right: 0,
  zIndex: 10,
  '& svg': {
    width: 32,
    height: 32
  }
}));

const Flex = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 16
}));

const CoursesCard = ({ item }) => {
  const t = useTranslate('Course');

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [needAuth, setNeedAuth] = React.useState(false);
  const [isFavorite, setIsFavorite] = React.useState(item?.isFavorite);

  const {
    _id,
    courseDuration,
    courseDurationDays,
    courseLanguage = [],
    studyFormat = [],
    minCostValue,
    maxCostValue,
    costCurrency,
    courseName,
    proficiency,
    startDate,
    platform: { name, logo } = {}
  } = item;

  const handleNavigate = React.useCallback(() => {
    if (!_id || !name) return;
    const slug = slugify(name || 'course');
    navigate(`/education/courses/${slug}/${_id}`);
  }, [name, _id, navigate]);

  const handleClose = React.useCallback((e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    setNeedAuth(false);
  }, []);

  const handleToggleFavorite = React.useCallback(
    (e) => {
      e.stopPropagation();
      e.preventDefault();

      if (!isAuthenticated()) {
        setNeedAuth(true);
        return;
      }

      const next = !isFavorite;
      setIsFavorite(next);

      if (next) {
        dispatch(addCourseToFavorites(_id));
      } else {
        dispatch(removeCourseFromFavorites(_id));
      }
    },
    [dispatch, _id, isFavorite]
  );

  const href = React.useMemo(() => {
    if (!_id || !name) return '#';
    const slug = slugify(name || 'course');
    return `/education/courses/${slug}/${_id}`;
  }, [name, _id]);

  return (
    <Card onClick={handleNavigate}>
      <NeedAuthDialog handleClose={handleClose} needAuth={needAuth} />
      <HiddenLink href={href} />
      <LinkStyled>
        <Flex>
          {logo && (
            <img
              src={`${logo}/public`}
              alt={name}
              style={{ height: 32, maxWidth: 65, objectFit: 'contain' }}
            />
          )}
          <LinkTitleStyled>{name}</LinkTitleStyled>
        </Flex>
        <ChevronIcon />
      </LinkStyled>

      <ListItemTitle>
        {courseName}
        <FavoriteButton onClick={handleToggleFavorite} aria-label="favorite" size="small">
          {isFavorite ? <CheckedStarIcon /> : <StarIcon />}
        </FavoriteButton>
      </ListItemTitle>

      <TagsWrapper>
        {proficiency.map((l, idx) => (
          <Tag key={`${l}-${idx}`} type={l}>
            {l}
          </Tag>
        ))}
      </TagsWrapper>

      <CourseAttributes
        duration={courseDurationDays}
        courseDuration={courseDuration}
        languages={courseLanguage}
        studyFormat={studyFormat}
        minCostValue={minCostValue}
        maxCostValue={maxCostValue}
        costCurrency={t(costCurrency || 'UAH')}
        startDate={startDate}
        size="card"
      />

      <ArrowContainedIconStyled />
    </Card>
  );
};

export default React.memo(CoursesCard);
